import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Heart, Brain, Droplets, FlaskConical, Syringe, ChevronDown, CheckCircle2 } from 'lucide-react';
const tomos = [{
  id: 'T1',
  title: 'ECG para la Guardia',
  desc: 'El método sistemático de 8 pasos + ritmos críticos + 10 casos resueltos',
  icon: Activity,
  image: 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?auto=format&fit=crop&w=800&q=80',
  highlights: ['Método de 8 pasos', 'Ritmos críticos', '10 casos resueltos'],
  accent: 'from-red-500 to-red-700'
}, {
  id: 'T2',
  title: 'Urgencias Cardiovasculares',
  desc: 'SCA, IC descompensada, crisis hipertensiva, TEP y disección aórtica',
  icon: Heart,
  image: 'https://images.unsplash.com/photo-1628348070889-cb656235b4eb?auto=format&fit=crop&w=800&q=80',
  highlights: ['Síndrome coronario agudo', 'Crisis hipertensiva', 'TEP y disección aórtica'],
  accent: 'from-rose-500 to-rose-700'
}, {
  id: 'T3',
  title: 'Urgencias Neurológicas',
  desc: 'ACV, convulsiones, cefalea de alarma, meningitis y coma',
  icon: Brain,
  image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80',
  highlights: ['ACV isquémico/hemorrágico', 'Estado epiléptico', 'Cefalea de alarma'],
  accent: 'from-purple-500 to-purple-700'
}, {
  id: 'T4',
  title: 'Shock — Diagnóstico y Manejo',
  desc: 'Los 4 tipos, algoritmos hemodinámicos y drogas vasoactivas',
  icon: Droplets,
  image: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=800&q=80',
  highlights: ['Los 4 tipos de shock', 'Drogas vasoactivas', 'Algoritmos hemodinámicos'],
  accent: 'from-orange-500 to-red-600'
}, {
  id: 'T5',
  title: 'Interpretación de Laboratorio Crítico',
  desc: 'Gasometría, ionograma, lactato, hemograma y marcadores de urgencia',
  icon: FlaskConical,
  image: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=800&q=80',
  highlights: ['Gasometría arterial', 'Ionograma y lactato', 'Marcadores de urgencia'],
  accent: 'from-teal-500 to-teal-700'
}, {
  id: 'T6',
  title: 'Procedimientos en Guardia',
  desc: 'Vías venosas, toracocentesis, punción lumbar, cardioversión y RCP',
  icon: Syringe,
  image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&w=800&q=80',
  highlights: ['Vías venosas centrales', 'Punción lumbar', 'Cardioversión y RCP'],
  accent: 'from-blue-500 to-blue-700'
}];
export function TomosSlider() {
  const [expandedId, setExpandedId] = useState<string | null>('T1');
  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50" data-id="element-375">
      <div className="max-w-5xl mx-auto" data-id="element-376">
        <div className="text-center mb-12" data-id="element-377">
          <div className="inline-block bg-medical-blue text-white font-bold px-4 py-1.5 rounded-full text-xs mb-4 tracking-widest" data-id="element-378">
            CONTENIDO COMPLETO
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-gray-900 mb-3" data-id="element-379">
            LOS 6 TOMOS
          </h2>
          <p className="text-lg text-gray-600 font-medium max-w-2xl mx-auto" data-id="element-380">
            Tu hoja de ruta visual para dominar cualquier urgencia en guardia
          </p>
        </div>

        {/* Vertical list of horizontal cards */}
        <div className="space-y-4" data-id="element-381">
          {tomos.map((tomo, index) => {
          const Icon = tomo.icon;
          const isExpanded = expandedId === tomo.id;
          return <motion.div key={tomo.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className={`bg-white rounded-2xl shadow-md border-2 overflow-hidden cursor-pointer transition-all ${isExpanded ? 'border-medical-blue shadow-xl' : 'border-gray-100 hover:border-gray-300 hover:shadow-lg'}`} onClick={() => toggleExpand(tomo.id)} data-id="element-382">
                {/* Horizontal row layout */}
                <div className="flex flex-row items-stretch" data-id="element-383">
                  {/* Image side */}
                  <div className="relative w-28 sm:w-44 md:w-56 shrink-0 overflow-hidden" data-id="element-384">
                    <img src={tomo.image} alt={tomo.title} className="w-full h-full object-cover" loading="lazy" data-id="element-385" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${tomo.accent} opacity-70 mix-blend-multiply`} data-id="element-386"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" data-id="element-387"></div>

                    {/* T# badge */}
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1 shadow-md" data-id="element-388">
                      <span className="font-heading font-black text-medical-blue text-lg md:text-xl" data-id="element-389">
                        {tomo.id}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="absolute bottom-3 right-3 bg-white/20 backdrop-blur-sm p-2 rounded-lg text-white border border-white/30" data-id="element-390">
                      <Icon size={20} data-id="element-391" />
                    </div>
                  </div>

                  {/* Content side */}
                  <div className="flex-1 p-4 md:p-5 flex flex-col justify-center" data-id="element-392">
                    <div className="flex justify-between items-start gap-3" data-id="element-393">
                      <div className="flex-1" data-id="element-394">
                        <span className="text-xs font-bold text-medical-blue uppercase tracking-wider mb-1 block" data-id="element-395">
                          Tomo {tomo.id.replace('T', '')}
                        </span>
                        <h3 className="font-heading font-bold text-gray-900 text-base md:text-xl leading-tight" data-id="element-396">
                          {tomo.title}
                        </h3>
                      </div>
                      <motion.div animate={{
                    rotate: isExpanded ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className={`shrink-0 p-1.5 rounded-full ${isExpanded ? 'bg-medical-blue text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-397">
                        <ChevronDown size={16} data-id="element-398" />
                      </motion.div>
                    </div>

                    <AnimatePresence data-id="element-399">
                      {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-400">
                          <div className="pt-3 mt-3 border-t border-gray-100" data-id="element-401">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-402">
                              {tomo.desc}
                            </p>
                            <div className="space-y-1.5" data-id="element-403">
                              {tomo.highlights.map((h, i) => <div key={i} className="flex items-center gap-2" data-id="element-404">
                                  <CheckCircle2 size={14} className="text-success-green shrink-0" data-id="element-405" />
                                  <span className="text-xs md:text-sm text-gray-700 font-medium" data-id="element-406">
                                    {h}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}